import { useEffect, useRef, useState } from 'react';

import {
    AUTH_PROVIDER_PARAM_KEY,
    AUTH_SOURCE_PARAM_KEY,
} from '~/modules/root/hook/auth-event-params';
import {
    BOOK_MEETING_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE,
} from '../constants';
import { useBookCta, useEnquireCta } from './use-page-ctas';

/** Which of the page's buttons a link asked to open. */
export interface CtaAutoOpenRequest {
    enquire: boolean;
    book: boolean;
}

export type CtaAutoOpenTarget = 'enquire' | 'book';

/**
 * The buttons a page URL asks to open, or null when it asks for none.
 *
 * Also null on the far side of a social sign-in: the gate that started the
 * round-trip resumes its own journey from the params and storage it left, and
 * a second opening would race it.
 */
export const readCtaAutoOpenRequest = (
    search: string,
): CtaAutoOpenRequest | null => {
    const params = new URLSearchParams(search);
    if (
        params.has(AUTH_SOURCE_PARAM_KEY) ||
        params.has(AUTH_PROVIDER_PARAM_KEY)
    ) {
        return null;
    }
    const request = {
        enquire:
            params.get(ENQUIRE_MODAL_SHOWUP_KEY) ===
            ENQUIRE_MODAL_SHOWUP_KEY_VALUE,
        book:
            params.get(BOOK_MEETING_SHOWUP_KEY) ===
            ENQUIRE_MODAL_SHOWUP_KEY_VALUE,
    };
    return request.enquire || request.book ? request : null;
};

/**
 * The one button to open: Book a meeting when asked for and shown, otherwise
 * the enquiry when asked for and shown. A button the visitor cannot see is
 * never opened, and never swapped for the other one unless both were asked.
 */
export const pickCtaAutoOpenTarget = (
    request: CtaAutoOpenRequest,
    visible: { enquire: boolean; book: boolean },
): CtaAutoOpenTarget | null => {
    if (request.book && visible.book) return 'book';
    if (request.enquire && visible.enquire) return 'enquire';
    return null;
};

/**
 * Drops the params a request was read from, replacing the history entry
 * rather than adding one, so a reload, Back or a copied link does not open the
 * popup again. Written straight to `history` so the page's loaders do not
 * re-run for a param none of them reads.
 */
const stripCtaAutoOpenParams = (request: CtaAutoOpenRequest) => {
    const url = new URL(window.location.href);
    if (request.enquire) url.searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
    if (request.book) url.searchParams.delete(BOOK_MEETING_SHOWUP_KEY);
    try {
        window.history.replaceState(window.history.state, '', url);
    } catch {
        // Safari rate-limits replaceState; the popup has opened regardless.
    }
};

/**
 * The page URL's auto-open request, read once after hydration. Null on the
 * server and on the first client render, so the markup matches.
 */
export const useCtaAutoOpenRequest = (): CtaAutoOpenRequest | null => {
    const [request, setRequest] = useState<CtaAutoOpenRequest | null>(null);
    useEffect(() => {
        setRequest(readCtaAutoOpenRequest(window.location.search));
    }, []);
    return request;
};

/**
 * Opens a detail page's CTA popup from its URL, for links from campaigns and
 * emails. Mounted once per page, inside the page's `PageCtasProvider`, by
 * `CtaAutoOpen`.
 *
 * - `?enquire=1` opens what the page's Enquire button opens: the enquiry
 *   popup with the page's form, label and context.
 * - `?book=1` opens what the page's Book a meeting button opens: the booking
 *   gate with the page's form and sessions (or the enquiry popup carrying the
 *   Book a meeting form when the gate is off).
 * - Only a button the visitor can see opens (audience rules, a switched-off
 *   Forms button, no Book a meeting on the page); otherwise nothing happens.
 * - With both, Book a meeting wins when shown, else the enquiry.
 * - Opens once per page load, then removes the params without a new history
 *   entry. Ignored on the return from a social sign-in.
 */
export const useCtaAutoOpen = (request: CtaAutoOpenRequest): void => {
    const enquire = useEnquireCta();
    const book = useBookCta();
    const handledRef = useRef(false);
    const { visible: enquireVisible, open: openEnquire } = enquire;
    const { visible: bookVisible, open: openBook } = book;

    useEffect(() => {
        if (handledRef.current) return;
        handledRef.current = true;
        const target = pickCtaAutoOpenTarget(request, {
            enquire: enquireVisible,
            book: bookVisible,
        });
        if (target === 'book') openBook();
        if (target === 'enquire') openEnquire();
        stripCtaAutoOpenParams(request);
    }, [request, enquireVisible, bookVisible, openEnquire, openBook]);
};
